import { createAction } from 'redux-actions';
import { actionKey } from './constants';
import type {
  GlobalUnreadsCountCallbackArgs,
  ChatroomsListSubscription,
  ChatroomsListSubscriptionCallbackParams,
  ChatroomSubscription,
} from '@wix/chat-sdk/lib/src/types/sdk-api.types';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import type { Message } from '@wix/chat-sdk';
import { ChatWindowMode } from '../ChatWindow/types';
import {
  NewChatParticipant,
  ChatroomsState,
  ChatroomScrollEvent,
  ChatParticipant,
  WixChatroom,
  NewChatRequestSource,
  ChatWorkerStatus,
  ChatWorkerIdleStatus,
} from './types';

// Misc
export const dropFirstTimeNoticeFlag = createAction(
  `${actionKey}.CHAT_DROP_FTUE_FLAG`,
  () => ({})
);

// Dynamic redux
export const reduxModuleLoaded = createAction(
  `${actionKey}.CHAT_REDUX_READY`,
  () => ({})
);

// Local storage
export const hydrateDBInit = createAction(
  `${actionKey}.CHAT_LS_HYDRATE_INIT`,
  (chatAuthToken: string) => ({
    chatAuthToken,
  })
);
export const hydrateDBChats = createAction(
  `${actionKey}.CHAT_LS_HYDRATE_CHATS`,
  (openChatroomsOrder: string[], chatrooms: ChatroomsState) => ({
    openChatroomsOrder,
    chatrooms,
  })
);
export const hydrateDBChatListCache = createAction(
  `${actionKey}.CHAT_LS_HYDRATE_CHATLIST_CACHE`,
  chats => ({
    chats,
  })
);
export const hydrateDBChatListRequestsCache = createAction(
  `${actionKey}.CHAT_LS_HYDRATE_CHATLIST_REQUESTS_CACHE`,
  chats => ({
    chats,
  })
);
export const setChatAuthToken = createAction(
  `${actionKey}.CHAT_CACHE_TOKEN`,
  token => ({ token })
);

// Main chat actions
export const chatOnline = createAction(`${actionKey}.CHAT_ONLINE`, () => ({}));
export const chatOffline = createAction(`${actionKey}.CHAT_OFFLINE`, error => ({
  error,
}));
export const setPageType = createAction(`${actionKey}.CHAT_PAGETYPE`, type => ({
  type,
}));
export const openChatPanel = createAction(
  `${actionKey}.CHAT_OPEN_CHATPANEL`,
  () => ({})
);
export const closeChatPanel = createAction(
  `${actionKey}.CHAT_CLOSE_CHATPANEL`,
  () => ({})
);

// Worker
export const chatWorkerReady = createAction(
  `${actionKey}.CHAT_WORKER_READY`,
  () => ({})
);
export const setChatWorker = createAction(
  `${actionKey}.CHAT_SET_WORKER`,
  (worker: Worker) => ({ worker })
);
export const setChatWorkerStatus = createAction(
  `${actionKey}.CHAT_SET_WORKER_STATUS`,
  (status: ChatWorkerStatus) => ({ status })
);
export const chatStartIdle = createAction(
  `${actionKey}.CHAT_START_IDLE`,
  () => ({})
);
export const chatEndIdle = createAction(
  `${actionKey}.CHAT_END_IDLE`,
  () => ({})
);
export const chatStartWake = createAction(
  `${actionKey}.CHAT_START_WAKE`,
  () => ({})
);
export const chatWorkerStartupIfNeeded = createAction(
  `${actionKey}.CHAT_WORKER_STARTUP`,
  (isInit: boolean) => ({ isInit })
);
export const setChatWorkerIdleStatus = createAction(
  `${actionKey}.CHAT_SET_IDLE_STATUS`,
  (idle: ChatWorkerIdleStatus) => ({ idle })
);

// Global unreads
export const globalUnreadCountSubscribe = createAction(
  `${actionKey}.UNREAD_COUNT_SUB`,
  () => ({})
);
export const globalUnreadCountUnSubscribe = createAction(
  `${actionKey}.UNREAD_COUNT_UNSUB`,
  () => ({})
);
export const globalUnreadCountUpdated = createAction(
  `${actionKey}.UNREAD_COUNT_UPDATE`,
  (data: GlobalUnreadsCountCallbackArgs) => data
);

// Chatlist
export const chatListSubscribe = createAction(
  `${actionKey}.CHATLIST_SUB`,
  () => ({})
);
export const chatListUnsubscribe = createAction(
  `${actionKey}.CHATLIST_UNSUB`,
  () => ({})
);
export const chatListSuccess = createAction(
  `${actionKey}.CHATLIST_SUCCESS`,
  (sub: ChatroomsListSubscription) => ({ sub })
);
export const chatListError = createAction(
  `${actionKey}.CHATLIST_ERROR`,
  error => ({ error })
);
export const chatListUpdated = createAction(
  `${actionKey}.CHATLIST_UPDATED`,
  (data: ChatroomsListSubscriptionCallbackParams) => ({ ...data })
);
export const chatListFetchMore = createAction(
  `${actionKey}.CHATLIST_FETCHMORE`,
  () => ({})
);
export const setActiveChatListTab = createAction(
  `${actionKey}.CHATLIST_SET_TAB`,
  index => ({ index })
);

// Chat Requests
export const chatRequestsSubscribe = createAction(
  `${actionKey}.CHAT_REQUESTS_SUB`,
  () => ({})
);
export const chatRequestsUnsubscribe = createAction(
  `${actionKey}.CHAT_REQUESTS_UNSUB`,
  () => ({})
);
export const chatRequestsSubscribeSuccess = createAction(
  `${actionKey}.CHAT_REQUESTS_SUBSCRIBE_SUCCESS`,
  (sub: ChatroomsListSubscription) => ({ sub })
);
export const chatRequestsSubscribeError = createAction(
  `${actionKey}.CHAT_REQUESTS_SUBSCRIBE_ERROR`,
  error => ({ error })
);
export const chatRequestsUpdated = createAction(
  `${actionKey}.CHAT_REQUESTS_UPDATED`,
  (data: ChatroomsListSubscriptionCallbackParams) => ({ ...data })
);
export const chatRequestsFetchMore = createAction(
  `${actionKey}.CHAT_REQUESTS_FETCH_MORE`,
  () => ({})
);
export const acceptChatRequest = createAction(
  `${actionKey}.ACCEPT_CHAT_REQUEST`,
  (chatroomId: string) => ({ chatroomId })
);

// Chatrooms
export const chatroomSubscribe = createAction(
  `${actionKey}.CHAT_SUB`,
  (chatroomId: string) => ({ chatroomId })
);
export const chatroomSubscriptionStarted = createAction(
  `${actionKey}.CHAT_SUB_START`,
  (chatroomId: string, sub: ChatroomSubscription) => ({ chatroomId, sub })
);
export const chatroomInitialFetched = createAction(
  `${actionKey}.CHAT_SUB_INIT_FETCHED`,
  (chatroomId: string, room: WixChatroom) => ({ chatroomId, room })
);
export const chatroomChanged = createAction(
  `${actionKey}.CHAT_SUB_ONCHANGE`,
  (chatroomId: string, room: WixChatroom) => ({ chatroomId, room })
);
export const fetchChatroomParticipants = createAction(
  `${actionKey}.FETCH_CHAT_PARTICIPANTS`,
  (chatroomId: string) => ({
    chatroomId,
  })
);
export const chatroomParticipantsFetched = createAction(
  `${actionKey}.CHAT_PARTICIPANTS_FETCHED`,
  (chatroomId: string, participants: ChatParticipant[]) => ({
    chatroomId,
    participants,
  })
);
export const chatroomError = createAction(
  `${actionKey}.CHAT_SUB_ERROR`,
  (chatroomId: string, error: string) => ({ chatroomId, error })
);

export const chatroomUnsubscribe = createAction(
  `${actionKey}.CHAT_UNSUB`,
  (chatroomId: string) => ({ chatroomId })
);

export const toggleOverlayChatList = createAction(
  `${actionKey}.TOGGLE_OVERLAY_CHAT_LIST`,
  () => ({})
);

export const finishFocusingInputField = createAction(
  `${actionKey}.FINISH_FOCUSING_INPUT_FIELD`,
  (chatroomId: string) => ({ chatroomId })
);

export const switchToChatroomInOverlayOverflow = createAction(
  `${actionKey}.SWITCH_TO_CHATROOM_IN_OVERLAY_OVERFLOW`,
  (overflowStartIndex: number, chatroomId: string) => ({
    overflowStartIndex,
    chatroomId,
  })
);

export const openOrSwitchToChatroom = createAction(
  `${actionKey}.OPEN_OR_TOGGLE_CHATROOM`,
  (chatroomId: string) => ({ chatroomId })
);

export const openChatroom = createAction(
  `${actionKey}.OPEN_CHATROOM`,
  (chatroomId: string) => ({ chatroomId })
);

/**
 * Opens a chatroom at the end of desktop's chatbar stack.
 *
 * Useful if we need to open a new chatroom without messing with the user's already open chatrooms.
 */
export const openChatroomAtEndOfStack = createAction(
  `${actionKey}.OPEN_CHATROOM_AT_END_OF_STACK`,
  (chatroomId: string) => ({ chatroomId })
);

/** Closes a chat window and cleans up its subscription */
export const closeChatroom = createAction(
  `${actionKey}.CLOSE_CHATROOM`,
  (chatroomId: string) => ({ chatroomId })
);

/** Toggles between `ChatWindowMode.DEFAULT` and `ChatWindowMode.MINIMIZED`. For the floating chat windows on desktop */
export const toggleChatroomWindow = createAction(
  `${actionKey}.TOGGLE_CHATROOM_WINDOW`,
  (chatroomId: string) => ({ chatroomId })
);

export const changeChatroomWindowMode = createAction(
  `${actionKey}.CHANGE_CHATROOM_WINDOW_MODE`,
  (chatroomId: string, mode: ChatWindowMode) => ({ chatroomId, mode })
);

export const addNewChatParticipant = createAction(
  `${actionKey}.ADD_NEW_CHAT_PARTICIPANT`,
  (participant: NewChatParticipant) => ({ participant })
);

export const removeNewChatParticipant = createAction(
  `${actionKey}.REMOVE_NEW_CHAT_PARTICIPANT`,
  (participant: NewChatParticipant) => ({ participant })
);

export const clearNewChatWindowParticipants = createAction(
  `${actionKey}.CLEAR_NEW_CHAT_WINDOW_PARTICIPANTS`,
  () => ({})
);

/**
 * Starts our new chat creation process and sets up a message to be sent upon creation.
 *
 * `requestId` is a temporary id that you provide so we can manage multiple new chat requests at the same time.
 *
 * Refer to the Chat README for context on why we need to handle multiple requests.
 */
export const createNewChatroom = createAction(
  `${actionKey}.CREATE_NEW_CHATROOM`,
  // createAction + typescript really doesn't like us adding an optional argument here,
  // so I removed the typings
  (requestId, message) => ({
    requestId,
    message,
  })
);

export const retryCreateNewChatroom = createAction(
  `${actionKey}.RETRY_CREATE_NEW_CHATROOM`,
  requestId => ({ requestId })
);

export const failCreatingNewChatroom = createAction(
  `${actionKey}.FAIL_CREATING_NEW_CHATROOM`,
  (requestId, error: string, errorKey: string) => ({
    requestId,
    error,
    errorKey,
  })
);

export const createChatWithUser = createAction(
  `${actionKey}.CREATE_CHAT_WITH_USER`,
  (
    requestId: string,
    source: NewChatRequestSource,
    user: PapiUser,
    message: Message | undefined
  ) => ({
    requestId,
    source,
    user,
    message,
  })
);

/**
 *
 */
export const createOrOpenChatWithUser = createAction(
  `${actionKey}.CREATE_OR_OPEN_CHAT_WITH_USER`,
  (requestId: string, user: PapiUser) => ({
    requestId,
    user,
  })
);

export const createOrOpenChatAndSend = createAction(
  `${actionKey}.CREATE_OR_OPEN_CHAT_AND_SEND`,
  (requestId: string, user: PapiUser, message: Message) => ({
    requestId,
    user,
    message,
  })
);

/**
 * Starts our new chat creation process using the participants from an existing chat.
 *
 * `requestId` is a temporary id that you provide so we can manage multiple new chat requests at the same time.
 *
 * Note: Currently we don't add users to an ongoing chat, we create a new chat with the new users
 * This may change. The UX isn't clear and we've heard several different things.
 */
export const createGroupChatFromDirectMessage = createAction(
  `${actionKey}.CREATE_GROUP_CHAT_FROM_DIRECT_MESSAGE`,
  (requestId: string, chatroomId: string) => ({ requestId, chatroomId })
);

export const addParticipantToChatUserInput = createAction(
  `${actionKey}.ADD_PARTICIPANT_TO_CHAT_USER_INPUT`,
  (chatroomId: string, participant: NewChatParticipant) => ({
    chatroomId,
    participant,
  })
);

export const removeParticipantFromChatUserInput = createAction(
  `${actionKey}.REMOVE_PARTICIPANT_FROM_CHAT_USER_INPUT`,
  (chatroomId: string, participant: NewChatParticipant) => ({
    chatroomId,
    participant,
  })
);

export const clearParticipantsFromChatUserInput = createAction(
  `${actionKey}.CLEAR_PARTICIPANTS_FROM_CHAT_USER_INPUT`,
  (chatroomId: string) => ({ chatroomId })
);

export const initializeNewChatroom = createAction(
  `${actionKey}.INITIALIZE_NEW_CHATROOM`,
  (requestId: string, chatroomId: string) => ({ requestId, chatroomId })
);

/** Handles clean up of any left over after a successful `initializeNewChatroom` */
export const finalizeNewChatroom = createAction(
  `${actionKey}.FINALIZE_NEW_CHATROOM`,
  (requestId: string) => ({ requestId })
);

export const openNewChatWindow = createAction(
  `${actionKey}.OPEN_NEW_CHAT_WINDOW`,
  (requestId: string) => ({ requestId })
);
export const closeNewChatWindow = createAction(
  `${actionKey}.CLOSE_NEW_CHAT_WINDOW`,
  (requestId: string) => ({ requestId })
);

export const beginSetupOfNewChatWithParticipant = createAction(
  `${actionKey}.BEGIN_SETUP_OF_NEW_CHAT_WITH_PARTICIPANT`,
  (requestId: string, participant: NewChatParticipant) => ({
    requestId,
    participant,
  })
);

export const startNewChatWithParticipant = createAction(
  `${actionKey}.START_NEW_CHAT_WITH_PARTICIPANT`,
  (requestId: string, participant: NewChatParticipant) => ({
    requestId,
    participant,
  })
);

export const leaveChatroom = createAction(
  `${actionKey}.LEAVE_CHATROOM`,
  (chatroomId: string, users?: string[], needsConfirmation?: boolean) => ({
    chatroomId,
    users,
    needsConfirmation,
  })
);

export const blockChatParticipant = createAction(
  `${actionKey}.BLOCK_CHAT_PARTICIPANT`,
  (user: PapiUser, chatroomId: string) => ({
    user,
    chatroomId,
  })
);

/** Clean up after a successful `leaveChatroom` action */
export const finishLeavingChatroom = createAction(
  `${actionKey}.FINISH_LEAVING_CHATROOM`,
  (chatroomId: string) => ({ chatroomId })
);

export const errorLeavingChatroom = createAction(
  `${actionKey}.ERROR_LEAVING_CHATROOM`,
  (chatroomId: string, error: string) => ({ chatroomId, error })
);

export const deleteChatroom = createAction(
  `${actionKey}.DELETE_CHATROOM`,
  (chatroomId, needsConfirmation?: boolean) => ({
    chatroomId,
    needsConfirmation,
  })
);

export const renameChatroom = createAction(
  `${actionKey}.RENAME_CHATROOM`,
  (chatroomId: string, name: string) => ({ chatroomId, name })
);

export const signalNewMsg = createAction(
  `${actionKey}.NEW_MSG_SIGNAL`,
  (chatroomId: string) => ({ chatroomId })
);

export const queueChatroomScrollEvent = createAction(
  `${actionKey}.SCROLL_CHATROOM`,
  (chatroomId: string, scrollEvent: ChatroomScrollEvent) => ({
    chatroomId,
    scrollEvent,
  })
);

export const clearChatroomScrollEvent = createAction(
  `${actionKey}.CLEAR_CHATROOM_SCROLL_EVENT`,
  (chatroomId: string) => ({ chatroomId })
);

/** Pull all the participants that have been added to a group chat's user input and try to add them to the current chat  */
export const addParticipantsToGroupChat = createAction(
  `${actionKey}.ADD_PARTICIPANTS_TO_GROUP_CHAT`,
  (chatroomId: string) => ({ chatroomId })
);

export const finishAddingParticipantsToGroupChat = createAction(
  `${actionKey}.FINISH_ADDING_PARTICIPANTS_TO_GROUP_CHAT`,
  (chatroomId: string) => ({ chatroomId })
);

export const watchChatParticipant = createAction(
  `${actionKey}.WATCH_CHAT_PARTICIPANT`,
  (username: string) => ({ username })
);

export const removeGroupChatParticipant = createAction(
  `${actionKey}.REMOVE_GROUP_CHAT_PARTICIPANT`,
  (chatroomId: string, participantId: string) => ({ chatroomId, participantId })
);

export const finishRemovingGroupChatParticipant = createAction(
  `${actionKey}.FINISH_REMOVING_GROUP_CHAT_PARTICIPANT`,
  (chatroomId: string, participantId: string) => ({ chatroomId, participantId })
);

export const errorRemovingGroupChatParticipant = createAction(
  `${actionKey}.ERROR_REMOVING_GROUP_CHAT_PARTICIPANT`,
  (chatroomId: string, error: string) => ({ chatroomId, error })
);

export const initializeChatroomModeManage = createAction(
  `${actionKey}.INITIALIZE_CHATROOM_MODE_MANAGE`,
  (chatroomId: string) => ({ chatroomId })
);

export const finalizeChatroomModeManage = createAction(
  `${actionKey}.FINALIZE_CHATROOM_MODE_MANAGE`,
  (chatroomId: string, participants: ChatParticipant[]) => ({
    chatroomId,
    participants,
  })
);

export const refreshChatListSubscription = createAction(
  `${actionKey}.REFRESH_CHAT_LIST_SUBSCRIPTION`,
  () => ({})
);

export const refreshChatroomSubscription = createAction(
  `${actionKey}.REFRESH_CHATROOM_SUBSCRIPTION`,
  (chatroomId: string) => ({ chatroomId })
);

export const sendDroppedDeviation = createAction(
  `${actionKey}.SEND_DROPPED_DEVIATION`,
  (chatroomId: string, deviation: PapiDeviation) => ({ deviation, chatroomId })
);

export const sendDeviationByDirectMessage = createAction(
  `${actionKey}.SEND_DEVIATION_BY_DIRECT_MESSAGE`,
  (user: PapiUser, deviation: PapiDeviation) => ({ deviation, user })
);

export const cacheChatroomScrollPosition = createAction(
  `${actionKey}.CACHE_CHATROOM_SCROLL_POSITION`,
  (chatroomId: string, scrollPosition: number) => ({
    chatroomId,
    scrollPosition,
  })
);

export const setNewMessageArrived = createAction(
  `${actionKey}.NEW_MESSAGE_ARRIVED`,
  () => ({})
);

/**
 * Brings an open chatroom to the front of desktop's chatbar.
 */
export const bringChatroomToFrontOfStack = createAction(
  `${actionKey}.BRING_CHATROOM_TO_FRONT_OF_STACK`,
  (chatroomId: string) => ({ chatroomId })
);

export const setFooterHeight = createAction(
  `${actionKey}.SET_FOOTER_HEIGHT`,
  (height, chatId) => ({ height, chatId })
);

export const startRealtimeListener = createAction(
  `${actionKey}.REALTIME_START`,
  () => ({})
);

export const stopRealtimeListener = createAction(
  `${actionKey}.REALTIME_STOP`,
  () => ({})
);

/**
 * Fired whenever we receive a message in any chatroom, regardless of if it was open at the time.
 *
 * Dispatched via `@wix/chat-sdk`'s `chatEventEmitter`
 */
export const receivedRealtimeMessage = createAction(
  `${actionKey}.RECEIVED_REALTIME_MESSAGE`,
  data => ({ data })
);

/**
 * Adds a chatroom id to a list of ids that we'll send to the `chat/shouldopenroom` endpoint.
 *
 * Fired whenever we see a new realtime message event and the chatroom fits some critera.
 */
export const addChatroomToNextShouldOpenRequest = createAction(
  `${actionKey}.ADD_CHATROOM_TO_NEXT_SHOULD_OPEN_REQUEST`,
  (chatroomId: string) => ({
    chatroomId,
  })
);

/**
 * Clears the list of batched ids that we have already sent to the `chat/shouldopenroom` endpoint.
 */
export const clearShouldOpenRequestIds = createAction(
  `${actionKey}.CLEAR_SHOULD_OPEN_REQUEST_IDS`,
  () => ({})
);

export const clearSdk = createAction(`${actionKey}.SDK_CLEAR`, () => ({}));
